import { useEffect, useState } from "react";
import {
  Box,
  ButtonBase,
  Collapse,
  Fade,
  Stack,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { Block, WebBackButton } from "../../components/shared";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTelegram } from "../../hooks";

const SUPPORT_URL =
  process.env.REACT_APP_SUPPORT_URL || "https://t.me/zero_wallet_support";

interface FaqSection {
  id: string;
  title: string;
  items: FaqItem[];
}
interface FaqItem {
  q: string;
  a: string;
}

const SECTION_IDS = [
  "about",
  "deposit_withdraw",
  "swap",
  "qr_sbp",
  "security",
  "referral",
] as const;

const ITEMS_PER_SECTION: Record<(typeof SECTION_IDS)[number], number> = {
  about: 3,
  deposit_withdraw: 4,
  swap: 3,
  qr_sbp: 2,
  security: 2,
  referral: 2,
};

export function FAQPage() {
  const navigate = useNavigate();
  const { tg } = useTelegram();
  const { t } = useTranslation("faq");
  const [openId, setOpenId] = useState<string | null>(null);

  const SECTIONS: FaqSection[] = SECTION_IDS.map((id) => ({
    id,
    title: t(`sections.${id}.title`),
    items: Array.from({ length: ITEMS_PER_SECTION[id] }, (_, i) => ({
      q: t(`sections.${id}.items.${i}.q`),
      a: t(`sections.${id}.items.${i}.a`),
    })),
  }));

  useEffect(() => {
    const handler = () => navigate("/profile");
    tg.BackButton.show();
    tg.onEvent("backButtonClicked", handler);
    return () => {
      tg.BackButton.hide();
      tg.offEvent("backButtonClicked", handler);
    };
  }, [navigate, tg]);

  return (
    <Fade in>
      <Box sx={{ marginBottom: "100px", width: "100%" }}>
        <WebBackButton to="/profile" title={t("title")} />

        <Stack spacing={2}>
          {SECTIONS.map((section) => (
            <Box key={section.id}>
              <Typography
                sx={{
                  fontSize: "12.5px",
                  fontWeight: 700,
                  opacity: 0.55,
                  letterSpacing: "0.4px",
                  textTransform: "uppercase",
                  paddingLeft: "4px",
                  marginBottom: "8px",
                }}
              >
                {section.title}
              </Typography>
              <Block sx={{ padding: "4px 14px" }}>
                <Stack divider={<HairlineDivider />} spacing={0}>
                  {section.items.map((it, idx) => {
                    const id = `${section.id}:${idx}`;
                    const isOpen = openId === id;
                    return (
                      <FaqRow
                        key={id}
                        item={it}
                        isOpen={isOpen}
                        onToggle={() => setOpenId(isOpen ? null : id)}
                      />
                    );
                  })}
                </Stack>
              </Block>
            </Box>
          ))}

          {/* CTA к поддержке */}
          <Block
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              cursor: "pointer",
            }}
            onClick={() => Telegram.WebApp.openTelegramLink(SUPPORT_URL)}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: "50%",
                backgroundColor: "primary.main",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon
                icon="solar:chat-round-dots-linear"
                width={20}
                height={20}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                {t("cta.title")}
              </Typography>
              <Typography
                sx={{ fontSize: "12px", opacity: 0.6, marginTop: "1px" }}
              >
                {t("cta.subtitle")}
              </Typography>
            </Box>
            <Icon
              icon="solar:alt-arrow-right-linear"
              width={18}
              height={18}
              color="#b0b0b0"
            />
          </Block>
        </Stack>
      </Box>
    </Fade>
  );
}

function FaqRow({
  item,
  isOpen,
  onToggle,
}: {
  item: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <Box>
      <ButtonBase
        onClick={onToggle}
        sx={{
          width: "100%",
          padding: "14px 0",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          textAlign: "left",
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            flex: 1,
            minWidth: 0,
            lineHeight: 1.4,
          }}
        >
          {item.q}
        </Typography>
        <Box
          sx={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Icon icon="solar:alt-arrow-down-linear" width={18} height={18} />
        </Box>
      </ButtonBase>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <Typography
          sx={{
            fontSize: "13px",
            opacity: 0.7,
            lineHeight: 1.55,
            paddingBottom: "14px",
          }}
        >
          {item.a}
        </Typography>
      </Collapse>
    </Box>
  );
}

function HairlineDivider() {
  return (
    <Box
      sx={{
        height: "1px",
        backgroundColor: (t) =>
          t.palette.mode === "dark"
            ? "rgba(255,255,255,0.05)"
            : "rgba(0,0,0,0.05)",
      }}
    />
  );
}
